import { Plus } from 'lucide-react'
import * as React from 'react'

import { useChannelSelection } from '@/hooks/use-channel-selection'
import { useDialogState } from '@/hooks/use-dialog-state'
import { useGroups } from '@/hooks/use-groups'

import { useAuth } from '@/contexts/auth-context'
import { Role } from '@/enums/role.enum'

import { CreateChannelDialog, DeleteChannelDialog, EditChannelDialog } from './channel-management-dialogs'
import GroupItem from './group-item'
import { CreateGroupOrChannelDialog, DeleteGroupDialog, EditGroupDialog } from './group-management-dialogs'
import { ReorderChannelsDialog } from './reorder-channels-dialog'
import UngroupedChannels from './ungrouped-channels'
import { Button } from '../ui/button'

import { Channel, GroupChannel } from '@/types/group-channel.type'

interface GroupChannelSidebarProps {
  onSelectChannel?: (channelId: string, groupId: string, channelName: string) => void
  currentChannelId?: string
  groupId?: string
}

export default function GroupChannelSidebar({
  onSelectChannel: onSelectChannelProp,
  currentChannelId,
  groupId: currentGroupId,
}: GroupChannelSidebarProps) {
  const { user } = useAuth()
  const { groups, ungroupedChannels, loading, error, toggleGroup } = useGroups(currentGroupId)
  const { activeChannelId, onSelectChannelId } = useChannelSelection({
    currentChannelId,
    groupId: currentGroupId,
    groups,
    ungroupedChannels,
    onSelectChannelProp,
    autoSelectFirst: true,
    loading,
  })
  const {
    editGroupDialogOpen,
    setEditGroupDialogOpen,
    createGroupDialogOpen,
    setCreateGroupDialogOpen,
    deleteGroupDialogOpen,
    setDeleteGroupDialogOpen,
    selectedGroup,
    editChannelDialogOpen,
    setEditChannelDialogOpen,
    createChannelDialogOpen,
    setCreateChannelDialogOpen,
    deleteChannelDialogOpen,
    setDeleteChannelDialogOpen,
    selectedChannel,
    setSelectedChannel,
    selectedGroupForChannel,
    setSelectedGroup,
    handleEditGroup,
    handleDeleteGroup,
    handleAddNewChannel,
    handleEditChannel,
    handleDeleteChannel,
  } = useDialogState()

  const onSelectChannel = (channel: Channel, groupId: string) => {
    setSelectedChannel(channel)
    onSelectChannelId(channel, groupId)
  }

  const onSelectUngroupedChannel = (channel: Channel) => {
    setSelectedChannel(channel)
    onSelectChannelId(channel, 'ungrouped')
  }

  const handleAddUngroupedChannel = () => {
    handleAddNewChannel(null)
  }

  const isAdmin = user?.roles.includes(Role.ADMIN)

  // Reorder channels dialog state
  const [reorderDialogOpen, setReorderDialogOpen] = React.useState(false)
  const [channelsToReorder, setChannelsToReorder] = React.useState<Channel[]>([])
  const [reorderGroupName, setReorderGroupName] = React.useState<string | undefined>(undefined)

  const handleReorderGroupChannels = React.useCallback((group: GroupChannel) => {
    setChannelsToReorder(group.channels)
    setReorderGroupName(group.name)
    setReorderDialogOpen(true)
  }, [])

  const handleReorderUngroupedChannels = React.useCallback(() => {
    setChannelsToReorder(ungroupedChannels)
    setReorderGroupName(undefined)
    setReorderDialogOpen(true)
  }, [ungroupedChannels])

  return (
    <div className="h-full bg-sidebar border-r border-sidebar-border flex flex-col justify-start flex-shrink-0 overflow-hidden">
      <div className="px-3 sm:px-4 pt-4 sm:pt-8 pb-3 sm:pb-4 border-b border-sidebar-border flex-shrink-0">
        <div className="flex items-center sm:justify-between gap-2">
          <h2 className="text-sm font-semibold text-sidebar-foreground">Channels</h2>
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreateGroupDialogOpen(true)}
              className="h-6 w-6 p-0 hover:bg-sidebar-accent"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-2 sm:py-4">
        {loading && <div className="px-3 sm:px-4 py-2 text-xs text-muted-foreground">Loading…</div>}
        {error && !loading && <div className="px-3 sm:px-4 py-2 text-xs text-red-600">{error}</div>}
        {!loading && !error && (
          <>
            <UngroupedChannels
              channels={ungroupedChannels}
              activeChannelId={activeChannelId}
              onSelectChannel={onSelectUngroupedChannel}
              onAddChannel={handleAddUngroupedChannel}
              onEditChannel={handleEditChannel}
              onDeleteChannel={handleDeleteChannel}
              onReorderChannels={isAdmin && ungroupedChannels.length > 1 ? handleReorderUngroupedChannels : undefined}
            />
            {groups.map(group => (
              <GroupItem
                key={group.id}
                group={group}
                activeChannelId={activeChannelId}
                onToggleGroup={toggleGroup}
                onSelectChannel={channel => onSelectChannel(channel, group.id)}
                onAddChannel={handleAddNewChannel}
                onEditGroup={handleEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onEditChannel={handleEditChannel}
                onDeleteChannel={handleDeleteChannel}
                onSelectGroup={setSelectedGroup}
                onReorderChannels={handleReorderGroupChannels}
              />
            ))}
          </>
        )}
      </div>
      {isAdmin && (
        <>
          <EditGroupDialog open={editGroupDialogOpen} onOpenChange={setEditGroupDialogOpen} group={selectedGroup} />
          <CreateGroupOrChannelDialog open={createGroupDialogOpen} onOpenChange={setCreateGroupDialogOpen} />
          <DeleteGroupDialog
            open={deleteGroupDialogOpen}
            onOpenChange={setDeleteGroupDialogOpen}
            group={selectedGroup}
          />

          <EditChannelDialog
            groupId={selectedGroup?.id}
            open={editChannelDialogOpen}
            onOpenChange={setEditChannelDialogOpen}
            channel={selectedChannel}
          />
          <CreateChannelDialog
            open={createChannelDialogOpen}
            onOpenChange={setCreateChannelDialogOpen}
            group={selectedGroupForChannel}
          />
          <DeleteChannelDialog
            open={deleteChannelDialogOpen}
            onOpenChange={setDeleteChannelDialogOpen}
            channel={selectedChannel}
          />
          <ReorderChannelsDialog
            open={reorderDialogOpen}
            onOpenChange={setReorderDialogOpen}
            channels={channelsToReorder}
            groupName={reorderGroupName}
          />
        </>
      )}
    </div>
  )
}
